import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SEEN_KEY = "coachly_seen_lead_ids";
const POLL_MS = 15000;

function loadSeen() {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function saveSeen(set) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(-500)));
  } catch {}
}

function burst() {
  // Short, celebratory confetti burst from the top-right corner so it reads as "something new arrived".
  const colors = ["#163325", "#d4a373", "#8c9b86", "#f9f8f6"];
  confetti({
    particleCount: 90,
    spread: 70,
    startVelocity: 42,
    origin: { x: 0.9, y: 0.1 },
    colors,
    scalar: 0.9,
    disableForReducedMotion: true,
  });
  setTimeout(() => {
    confetti({
      particleCount: 60,
      spread: 55,
      startVelocity: 35,
      origin: { x: 0.9, y: 0.15 },
      colors,
      disableForReducedMotion: true,
    });
  }, 180);
}

/**
 * Polls /api/leads for the authenticated coach and fires a toast + confetti burst
 * the first time a new lead ID appears. "Seen" IDs are tracked in localStorage so
 * reloads/navigation don't re-trigger for the same lead.
 */
export function useNewLeadWatcher({ enabled = true } = {}) {
  const seenRef = useRef(null);
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    seenRef.current = loadSeen();

    const tick = async () => {
      try {
        const r = await api.get("/leads");
        const leads = Array.isArray(r.data) ? r.data : (r.data?.leads || []);
        if (cancelled) return;
        const seen = seenRef.current;

        // First poll after mount: just absorb existing IDs so we don't celebrate historical leads.
        if (!bootstrappedRef.current) {
          leads.forEach((l) => l?.id && seen.add(l.id));
          saveSeen(seen);
          bootstrappedRef.current = true;
          return;
        }

        const fresh = leads.filter((l) => l?.source === "chatbot" && l?.id && !seen.has(l.id));
        if (fresh.length > 0) {
          fresh.forEach((l) => seen.add(l.id));
          saveSeen(seen);
          burst();
          const latest = fresh[0];
          const name = latest?.name || "someone";
          toast.success(`New lead captured! ${name} just dropped their email.`, {
            description: fresh.length > 1 ? `+${fresh.length - 1} more also came in just now.` : latest?.email,
            duration: 6000,
            action: {
              label: "View",
              onClick: () => {
                window.location.href = `/app/leads/${latest.id}`;
              },
            },
          });
        }
      } catch {
        // silent — this is a background enhancement
      }
    };

    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled]);
}
